import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { GrupoProductos, GrupoProductosPagingRes } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError } from '@/shared';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchGrupoProductos = ({
  page = 1,
  page_size = 10,
  nombre = '',
}: GetGrupoProductosParams) => {
  return useQuery({
    queryKey: ['grupo-productos', page, nombre, page_size],
    queryFn: async () => {
      return getGrupoProductos({ page, nombre, page_size });
    },
  });
};

export const useGetGrupoProducto = (id: string) => {
  return useQuery({
    queryKey: ['grupo-productos', id],
    queryFn: async () => {
      return getGrupoProducto(id);
    },
  });
};

export const useCreateGrupoProductos = (navigate: NavigateFunction, returnUrl: string,
  enableNavigation = true) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createGrupoProductos,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grupo-productos'] });
      enableNavigation && navigate(returnUrl, { replace: true });
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

export const useUpdateGrupoProductos = (navigate: NavigateFunction, returnUrl: string, noState?: boolean,
  enableNavigation?: boolean) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateGrupoProductos,
    onSuccess: () => {
      noState &&  queryClient.invalidateQueries({ queryKey: ['grupo-productos'] });
      enableNavigation && navigate(returnUrl, { replace: true });
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

///* axios
export type GetGrupoProductosParams = {
  page?: number;
  page_size?: number;
  nombre?: string;
};

export type CreateGrupoProductosParams = Omit<GrupoProductos, 'id_grupo_producto'>;
export type UpdateGrupoProductosParams = {
  id: string;
  data: CreateGrupoProductosParams;
};

export const getGrupoProductos = async (params?: GetGrupoProductosParams) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<GrupoProductosPagingRes>(
    `${urlBackend}api/v1/grupo-producto/?${queryParams}`,
    true
  );
};

export const getGrupoProducto = async (id: string) => {
  try{
    return await get<GrupoProductos>(`${urlBackend}api/grupo-producto/${id}/`, true);
  }
  catch(error){
    return handleAxiosError(error);
  }
};

export const createGrupoProductos = async (data: CreateGrupoProductosParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<GrupoProductos>(`${urlBackend}api/v1/grupo-producto/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};

export const updateGrupoProductos = async ({ id, data }: UpdateGrupoProductosParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<GrupoProductos>(`${urlBackend}api/v1/grupo-producto/${id}/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};
